import { useContext, useEffect, useState } from "react"
import {
  // BrowserRouter as Router,
  Routes,
  Route,
  useNavigate,
} from "react-router-dom"
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { useDispatch, useSelector } from "react-redux"
import { ToastContainer, toast } from "react-toastify"
import { useCookies } from "react-cookie"
import { useIntl } from "react-intl"
import { LanguageContext } from "./i18n/LanguageProvider"

import { Spinner } from "./components"

import "react-toastify/dist/ReactToastify.css"

import {
  AuthenticatedLayout,
  BasicLayout,
  CreateAccountLayout,
} from "./Layouts"

import {
  PROTECTED_ROUTES_ORGANIZATION,
  UNPROTECTED_ROUTES,
  CREATE_ACCOUNT_ROUTES,
} from "./routes"
import { me } from "./services/auth"
import { getLanguage } from "./services/team"
import { useFetchAppConfig } from "./hooks/useFetchAppConfig"

import { loginAction } from "./redux/slices/authSlice"

import { ReactGA } from "./components"
import "./styles/main.scss"

const DEFAULT_LANGUAGE = "en";

function App() {
  const dispatch = useDispatch()
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const [cookies, setCookie, removeCookie] = useCookies(["token"]);

  const [init, setInit] = useState(false)
  const { language, prevLanguage, changeLanguage } = useContext(LanguageContext)

  const auth = useSelector((state) => state.auth)

  useFetchAppConfig();

  const PROTECTED_ROUTES = PROTECTED_ROUTES_ORGANIZATION

  // Show toast when language is updated
  useEffect(() => {
    if (prevLanguage && language !== prevLanguage) {
      toast.success(
        formatMessage({ id: "toast.languageUpdatedSuccessfully" }),
      )
    }
  }, [language, prevLanguage, formatMessage]);

  useEffect(() => {
    if (cookies.token) {
      me({ token: cookies.token })
        .then((data) => {
          dispatch(loginAction(data))
          setCookie("token", data.token, {
            path: "/",
            expires: new Date(Date.now() + 24 * 60 * 60 * 1000), // 24 hours from now
          })

          if (data.data?.onboardingStage === "AccountCreation") {
            navigate("/company-info")
          } else if (
            data.data?.onboardingStage === "ChoosePrice" &&
            !data.data.team?.pendingSinglePurchaseInvoice &&
            !data.data.team?.pendingSetupIntent &&
            !data.data.team?.pendingSubscriptionInvoice
          ) {
            navigate("/choose-pricing")
          } else if (data.data?.onboardingStage === "ChoosePrice") {
            navigate("/pending-payment")
          }

          setInit(true)
        })
        .catch(() => {
          removeCookie("token", {
            path: "/",
          })
          setInit(true)
        })
    } else {
      setInit(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (auth.isAuthenticated) {
      // Load language from user setting
      getLanguage()
        .then((data) => changeLanguage(data.language))
        .catch((error) => {
          console.error(error)
          changeLanguage(DEFAULT_LANGUAGE)
        });
    }
  }, [auth.isAuthenticated, changeLanguage, dispatch])

  if (!init) {
    return (
      <section className="app-loading">
        <Spinner />
      </section>
    );
  }

  return (
    <>
      <ReactGA />

      <Routes>
        {UNPROTECTED_ROUTES.map(({ path, El, layoutProps = {} }) => (
          <Route
            key={path}
            path={path}
            element={<BasicLayout {...layoutProps}>{El}</BasicLayout>}
          />
        ))}

        {CREATE_ACCOUNT_ROUTES.map(({ path, El, layoutProps = {} }) => (
          <Route
            key={path}
            path={path}
            element={
              <CreateAccountLayout {...layoutProps}>{El}</CreateAccountLayout>
            }
          />
        ))}

        {PROTECTED_ROUTES?.map(
          ({
            path,
            El,
            roles,
            Sidebar,
            allowOnboardingUsers,
            hideForTrialingUsers,
          }) => (
            <Route
              key={path}
              path={path}
              element={
                <AuthenticatedLayout
                  Sidebar={Sidebar}
                  roles={roles}
                  allowOnboardingUsers={allowOnboardingUsers}
                  hideForTrialingUsers={hideForTrialingUsers}
                >
                  {El}
                </AuthenticatedLayout>
              }
            />
          ),
        )}
      </Routes>

      <ToastContainer />

      <ReactQueryDevtools initialIsOpen={false} />
    </>
  )
}

export default App
